import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import "../Accounts/ledger-page.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import useApi from "../../hook/useApi";
import Paggination from "../Paggination";
import Loader from "../Loader/Loader";

const UserEditLogLayer = () => {
  const { AsyncGetApiCall } = useApi();

  const [searchTerm, setSearchTerm] = useState("");
  const [limitMode, setLimitMode] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);

  const [pageMeta, setPageMeta] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const limitVal = limitMode === "all" ? 1000 : Number(limitMode);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { pageNo, limitVal };

      if (searchTerm.trim()) {
        payload.search = searchTerm.trim();
      }

      const result = await AsyncGetApiCall("/users/edit-logs", payload);

      if (result?.success) {
        setRows(Array.isArray(result.data) ? result.data : []);
        setPageMeta({
          total: Number(result.total) || 0,
          totalPages: Number(result.totalPages) || 0,
          currentPage: Number(result.currentPage) || pageNo,
          hasNextPage: Boolean(result.hasNextPage),
          hasPrevPage: Boolean(result.hasPrevPage),
        });
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Failed to fetch edit logs", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [AsyncGetApiCall, pageNo, limitVal, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLogs();
    }, 350);

    return () => clearTimeout(timer);
  }, [fetchLogs]);

  const total = pageMeta.total || rows.length;

  const handleClear = () => {
    setSearchTerm("");
    setPageNo(1);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString();
  };

  const startEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + 1;
  const endEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + rows.length;

  return (
    <>
      {loading && <Loader />}
      <div className="card border shadow-sm radius-16 ledger-table-card">
        {/* Header */}
        <div className="card-body p-20 border-bottom" style={{ zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3">
            <div className="d-flex align-items-end gap-2 flex-wrap">
              {/* Show limit */}
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary-light">Show</span>
                <div style={{ width: "78px" }}>
                  <SearchableSelect
                    placeholder="All"
                    value={limitMode}
                    showSearch={false}
                    onChange={(e) => {
                      setLimitMode(e.target.value);
                      setPageNo(1);
                    }}
                    options={PAGE_SIZE_OPTIONS.map((n) => ({
                      label: String(n),
                      value: String(n),
                    }))}
                  />
                </div>
              </div>

              {/* Clear */}
              <button
                type="button"
                onClick={handleClear}
                className="btn btn-sm radius-8 px-16 py-10 btn-danger-600"
                disabled={loading}
              >
                Clear
              </button>
            </div>

            {/* Search */}
            <div
              className="position-relative flex-grow-1"
              style={{ maxWidth: "280px" }}
            >
              <Icon
                icon="ion:search-outline"
                className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
              />
              <input
                type="search"
                className="form-control ps-40 h-40-px radius-12"
                placeholder="Search details or model..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(1);
                }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive p-24">
          <table className="table bordered-table sm-table mb-0 text-nowrap">
            <thead>
              <tr className="bg-neutral-100">
                <th>SR No</th>
                <th>Client</th>
                <th>Details</th>
                <th>Changed By</th>
                <th>IP Address</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    No data available
                  </td>
                </tr>
              ) : (
                rows.map((entry, index) => (
                  <tr key={entry._id}>
                    <td>{(pageMeta.currentPage - 1) * limitVal + index + 1}</td>

                    <td>
                      {entry.userId?.name || "—"} (
                      {entry.userId?.loginId || "—"})
                    </td>

                    <td>{entry.details || "—"}</td>

                    <td>
                      {entry.editedBy?.name || entry.editedBy?.masterName || "—"} (
                      {entry.editedByModel || "—"})
                    </td>

                    <td>{entry.ipAddress || "—"}</td>

                    <td>{formatDateTime(entry.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Paggination
            total={total}
            totalPages={pageMeta.totalPages}
            currentPage={pageMeta.currentPage}
            setPageNo={setPageNo}
            startFrom={startEntry}
            endTo={endEntry}
          />
        </div>
      </div>
    </>
  );
};

export default UserEditLogLayer;
